import { Box, Divider, Grid } from "@mui/material";
import { ReactComponent as ActionNumber } from "assets/ActionNumber.svg";
import { ReactComponent as DeleteIcon } from "assets/DeleteIcon.svg";
import { ReactComponent as EditIcon } from "assets/EditIcon.svg";

import ThemeContext from "components/app/Context";
import MetricDetails from "components/features/dashboard/MetricDetails";
import { fetchBookmarkPlansIdAsync } from "components/features/library/Bookmark/Bookmark.slice";
import InvalidPlanButton from "components/features/library/Suggested/InvalidPlanButton";
import { fetchMyPlanDetailAsync } from "components/features/MyPlanDetails/MyPlanDetails.slice";
import DeleteDialog from "components/features/MyPlans/Delete";
import Edit from "components/features/MyPlans/Edit";
import { setisBookmarked } from "components/features/planDetails/PlanDetail.slice";
import { useTranslate } from "hooks/useTranslate";
import { useContext, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { accessTypes } from "utils/CommonConstants";
import { getHoursAndMin, getPlanDetail } from "utils/CommonFunctions";
import BookmarkPlan from "../Bookmark";
import Button from "../Button";
import FunctionalAreas from "../FunctionalAreas";
import InfoBar from "../InfoBar";
import KebabMenu from "../KebabMenu";
import PlanCard from "../PlanCard";
import PlanType from "../PlanType";
import Timing from "../Timing";
import Typography from "../Typography";
import AssignPlanDialog from "./AssignPlanDialog";

interface Props {
  item: any;
  isBookmarked?: boolean;
  suggested?: boolean;
  redirectFromDashboard?: boolean;
  individualPropertyInncode?: any;
}

const MyPlanBadge = (props: any) => {
  const { isMobileScreen } = useContext(ThemeContext);
  const { data } = props;

  if (data.myPlans && data.myPlans[0]?.title === data?.name) {
    return (
      <Typography
        variant={isMobileScreen ? "body1" : "label"}
        sx={{
          marginBottom: "4px",
          "&::before": {
            content: '""',
            display: "inline-block",
            height: "8px",
            width: "8px",
            borderRadius: "100%",
            backgroundColor: tokens.Primary.Primary400,
            marginRight: "4px"
          }
        }}
      >
        In My plans
      </Typography>
    );
  }

  return null;
};

function Item(props: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const translate = useTranslate();
  const {
    item,
    isBookmarked = false,
    suggested,
    redirectFromDashboard = false,
    individualPropertyInncode = ""
  } = props;
  const [searchParams] = useSearchParams();
  const [openEditPlan, setOpenEditPlan] = useState(false);
  const metricsPlansList: any = useAppSelector((state) => state.suggestedPlans.plansMetrics);
  const metrics = suggested && getPlanDetail(item?.id, metricsPlansList)?.metrics;
  const { isMobileScreen } = useContext(ThemeContext);
  const [openAssignPlan, setOpenAssignPlan] = useState(false);
  const { isSmeUser, isAbovePropertyUser } = useAppSelector((state) => state.app);
  const [openDeleteCapthaDialog, setOpenDeleteCapthaDialog] = useState(Boolean);
  const showActionsForAboveProp = isAbovePropertyUser && location.pathname.split("/")[1] === "myplans";
  const isTemplatePlan = searchParams.get("parentTab") === translate("common.templatePlans");
  const planMenu = [
    {
      label: "Assign Plan",
      enabled: true,
      action: async () => {
        setOpenAssignPlan(true);
      }
    }
  ];

  const aboveUserPlanMenu = [
    {
      label: "Edit Plan",
      accessType: accessTypes.update,
      icon: EditIcon,
      enabled: true,
      action: async () => {
        await dispatch(fetchMyPlanDetailAsync(item?.id));
        setOpenEditPlan(true);
      }
    },
    {
      label: "Delete Plan",
      icon: DeleteIcon,
      accessType: accessTypes.delete,
      enabled: true,
      action: async () => {
        setOpenDeleteCapthaDialog(true);
      }
    }
  ];

  const getViewByName = (viewName?: string) => {
    if (viewName === "Metrics")
      return (
        metrics &&
        Object.keys(metrics)?.map((metric: any) => {
          return <MetricDetails key={metric} metric={metrics[metric]} metricsBar={false} />;
        })
      );
    else
      return (
        <Box marginBottom={"12px"}>
          <PlanType
            planType={item?.planType}
            brandDeadlineDate={item?.brandDeadlines?.[0]?.deadline}
            initiativeName={item?.initiativeName}
          />
        </Box>
      );
  };

  const onViewDetailsButtonClick = () => {
    dispatch(setisBookmarked(isBookmarked));
    dispatch(fetchBookmarkPlansIdAsync());
    navigate(showActionsForAboveProp ? `/myplandetails/${item?.id}` : `/plandetails/${item.id}`, {
      state: {
        innCode: params?.inncode,
        redirectFromSuggested: suggested,
        redirectFromDashboard: redirectFromDashboard,
        individualPropertyInncode: individualPropertyInncode
      }
    });
  };

  return (
    <>
      <PlanCard>
        {isMobileScreen ? (
          <>
            <Grid gap={"8px"} item xs={12} display="flex" alignItems={"center"} justifyContent={"flex-start"}>
              {!isTemplatePlan && (
                <>
                  <BookmarkPlan cmsId={item.id} isBookmarked={isBookmarked} />
                  <Divider
                    sx={{
                      height: "12px",
                      width: "1px",
                      backgroundColor: tokens.Border.General.Secondary
                    }}
                    orientation="vertical"
                  ></Divider>
                </>
              )}
              <Timing duration={getHoursAndMin(item?.timeEstimateMinutes)} />
              {isSmeUser && (
                <Box sx={{ marginLeft: "auto" }}>
                  <KebabMenu options={planMenu} />
                </Box>
              )}
              {showActionsForAboveProp && <KebabMenu sx={{ marginLeft: "auto" }} options={aboveUserPlanMenu} />}
              {suggested && (
                <Box sx={{ marginLeft: "auto" }}>
                  <InvalidPlanButton planName={item.name} cmsId={item.id} />
                </Box>
              )}
            </Grid>
            {item?.planType || suggested ? (
              <Grid
                gap={"8px"}
                marginBottom={"-25px"}
                item
                xs={6}
                display="flex"
                alignItems={"baseline"}
                justifyContent={"flex-start"}
              >
                {getViewByName()}
              </Grid>
            ) : (
              <Grid item xs={6} />
            )}
            <Grid item xs={12}>
              <MyPlanBadge data={item} />
              <Typography variant={"body1"} color={tokens.Text.General.PrimaryHeadline} showBold>
                {item.name ?? item.title}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <FunctionalAreas functionalAreaIds={item.functionalAreaIds} />
            </Grid>
            <Grid item xs={6} display="flex" alignItems="center">
              <Box marginLeft="auto">
                <Button variant="secondary" height="34px" onClick={onViewDetailsButtonClick} label="View Details" />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <InfoBar
                label="Contain"
                logo={<ActionNumber />}
                value={`${item?.numCards ?? item?.totalCards} Actions`}
              ></InfoBar>
            </Grid>
            {suggested && (
              <Grid item xs={12} display="flex" flexDirection="column" gap={1}>
                {getViewByName("Metrics")}
              </Grid>
            )}
          </>
        ) : (
          <>
            {item?.planType || suggested ? (
              <Grid item md={9} marginBottom={"-28px"}>
                {getViewByName()}
              </Grid>
            ) : (
              <Grid item md={9} />
            )}

            <Grid gap={"8px"} item md={3} display="flex" alignItems={"center"} justifyContent={"flex-end"}>
              {!isTemplatePlan && (
                <>
                  <BookmarkPlan cmsId={item.id} isBookmarked={isBookmarked} />
                  <Divider
                    sx={{
                      height: "12px",
                      width: "1px",
                      backgroundColor: tokens.Border.General.Secondary
                    }}
                    orientation="vertical"
                  ></Divider>
                </>
              )}

              <Timing duration={getHoursAndMin(item?.timeEstimateMinutes)} />
              {isSmeUser && <KebabMenu options={planMenu} />}
              {showActionsForAboveProp && <KebabMenu options={aboveUserPlanMenu} />}
              {suggested && <InvalidPlanButton planName={item.name ?? item.title} cmsId={item.id} />}
            </Grid>
            <Grid item md={10}>
              <MyPlanBadge data={item} />
              <Typography variant={"h5"} color={tokens.Text.General.PrimaryHeadline} showBold>
                {item.name ?? item.title}
              </Typography>
            </Grid>
            <Grid item md={12}>
              <FunctionalAreas functionalAreaIds={item.functionalAreaIds} />
            </Grid>
            <Grid item md={12} display="flex" flexWrap="wrap" alignItems="center">
              <Box width="80%" display="flex" flexWrap="wrap" alignItems="center" gap={1}>
                {" "}
                <InfoBar
                  label="Contain"
                  logo={<ActionNumber />}
                  value={`${item?.numCards ?? item?.totalCards} Actions`}
                ></InfoBar>
                {getViewByName("Metrics")}
              </Box>
              <Box marginLeft="auto">
                <Button variant="secondary" onClick={onViewDetailsButtonClick} label="View Plan Details" />
              </Box>
            </Grid>
          </>
        )}
      </PlanCard>
      {openAssignPlan && (
        <AssignPlanDialog setOpenAssignPlanDialog={setOpenAssignPlan} selectedPlan={item}></AssignPlanDialog>
      )}
      {openEditPlan && (
        <Edit isTemplateType={isTemplatePlan} openEditPlan={openEditPlan} setOpenEditPlan={setOpenEditPlan} />
      )}
      {openDeleteCapthaDialog && (
        <DeleteDialog
          openDeleteCapthaDialog={openDeleteCapthaDialog}
          setOpenDeleteCapthaDialog={setOpenDeleteCapthaDialog}
          plan={item}
        />
      )}
    </>
  );
}

export default Item;
